import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';
import { FiFilter, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const AuditLog = () => {
  const { error: showError } = useToast();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entityType: '', action: '' });
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = () => {
    setLoading(true);
    const params = {};
    if (filters.entityType) params.entityType = filters.entityType;
    if (filters.action) params.action = filters.action;
    apiClient.get('/audit/logs', { params })
      .then(({ data }) => setLogs(data.data))
      .catch((err) => showError(getErrorMessage(err, 'Could not load audit log')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    apiClient.get('/organisations/current/users')
      .then(({ data }) => {
        const map = {};
        data.data.forEach((u) => { map[u.userId] = `${u.firstName} ${u.lastName}`; });
        setUsers(map);
      })
      .catch(() => { /* falls back to raw user id — non-critical */ });
    loadLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilter = (e) => {
    e.preventDefault();
    loadLogs();
  };

  const getActionColor = (action) => {
    const colors = {
      created: '#10b981', updated: '#3b82f6', deleted: '#ef4444',
      approved: '#10b981', locked: '#6b7280', voided: '#ef4444', reversed: '#f59e0b',
    };
    return colors[action] || '#6b7280';
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Audit Log</h1>
        <p>Who changed what, and when</p>
      </div>

      <section className="page-section" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e' }}>
          Only a representative set of sensitive actions are currently logged here (organisation profile
          updates, employee updates/archival, invoice voiding, journal reversal) — not every write across
          the whole system yet. This is a deliberate, flagged phase-1 scope, not a bug.
        </p>
      </section>

      <section className="page-section">
        <form onSubmit={handleFilter} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Entity Type</label>
            <input value={filters.entityType} onChange={(e) => setFilters(p => ({ ...p, entityType: e.target.value }))} placeholder="e.g. employee, invoice" />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label>Action</label>
            <select value={filters.action} onChange={(e) => setFilters(p => ({ ...p, action: e.target.value }))}>
              <option value="">All</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="deleted">Deleted</option>
              <option value="approved">Approved</option>
              <option value="locked">Locked</option>
              <option value="voided">Voided</option>
              <option value="reversed">Reversed</option>
            </select>
          </div>
          <button type="submit" className="btn btn-outline" style={{ width: 'auto' }}>
            <FiFilter size={16} /> Filter
          </button>
        </form>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th></th><th>When</th><th>User</th><th>Action</th><th>Entity</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No matching entries</td></tr>
              ) : logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}>
                    <td>{expandedId === log.id ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}</td>
                    <td>{new Date(log.createdAt).toLocaleString()}</td>
                    <td>{users[log.userId] || log.userId || 'System'}</td>
                    <td>
                      <span className="badge" style={{ background: getActionColor(log.action), color: 'white' }}>{log.action}</span>
                    </td>
                    <td>{log.entityType} #{log.entityId}</td>
                  </tr>
                  {expandedId === log.id && (
                    <tr>
                      <td colSpan={5} style={{ background: '#f9fafb', padding: '1rem' }}>
                        {log.reason && <p style={{ margin: '0 0 0.75rem 0' }}><strong>Reason:</strong> {log.reason}</p>}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem' }}>Before</div>
                            <pre style={{ fontSize: '0.75rem', background: 'white', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                              {log.oldValues ? JSON.stringify(JSON.parse(log.oldValues), null, 2) : '—'}
                            </pre>
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: '0.3rem', fontSize: '0.85rem' }}>After</div>
                            <pre style={{ fontSize: '0.75rem', background: 'white', padding: '0.5rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                              {log.newValues ? JSON.stringify(JSON.parse(log.newValues), null, 2) : '—'}
                            </pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </motion.div>
  );
};

export default AuditLog;